import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Avatar,
  Button,
  Alert,
  Skeleton,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  SportsSoccer,
  People,
  CalendarToday,
  FlashOn,
  CheckCircle,
  PersonAdd,
  EmojiEvents,
  LibraryAdd,
  Settings,
  PostAdd,
  Close as CloseIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrganizationChange } from '@/hooks/useOrganizationChange';
import { useAppSelector } from '@/hooks/redux';
import dayjs from 'dayjs';
import { playerService } from '@/services/playerService';
import { PlayerAvatar } from '@/components/player/PlayerAvatar';
import { scheduleService } from '@/services/scheduleService';
import { conflictService } from '@/services/conflictService';
import { globalLeagueCatalogService } from '@/services/globalLeagueService';
import { standingsService } from '@/services/standingsService';
import { LeagueLogo } from '@/components/LeagueLogo';
import WeeklyCalendar from '@/components/WeeklyCalendar';
import LeaguePerformanceCarousel from '@/components/LeaguePerformanceCarousel';
import ScoreboardStrip from '@/components/dashboard/ScoreboardStrip';
import StandingsBarList from '@/components/StandingsBarList';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [welcomeDialogOpen, setWelcomeDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAppSelector((state) => state.auth);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Check for welcome parameter
  useEffect(() => {
    if (searchParams.get('welcome') === 'true') {
      setWelcomeDialogOpen(true);
      // Remove the welcome parameter from URL
      searchParams.delete('welcome');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Refresh org-specific queries when organization changes
  useOrganizationChange(() => {
    queryClient.invalidateQueries(['players']);
    queryClient.invalidateQueries(['upcoming-match-summary']);
    queryClient.invalidateQueries(['conflict-count']);
    queryClient.invalidateQueries(['standings-dashboard']);
    queryClient.invalidateQueries(['league-catalog']);
  });

  // Prefetch league catalog so "Add League" dialog opens instantly
  useEffect(() => {
    queryClient.prefetchQuery(
      ['league-catalog', ''],
      () => globalLeagueCatalogService.getCatalog(),
      { staleTime: 5 * 60 * 1000 }
    );
  }, [queryClient]);

  // Fetch players
  const { data: players = [], isLoading: playersLoading } = useQuery(
    ['players', user?.currentOrganizationId],
    playerService.getPlayers,
    {
      enabled: !!user?.currentOrganizationId,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );

  // Combined match summary — count + next match in a single request
  const { data: matchSummary, isLoading: matchSummaryLoading } = useQuery(
    ['upcoming-match-summary', user?.currentOrganizationId],
    () => scheduleService.getUpcomingMatchSummary(365),
    {
      enabled: !!user?.currentOrganizationId,
      keepPreviousData: true,
      staleTime: 10 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
    }
  );

  // Fast conflict count for scoreboard — reads from cache, never blocks on computation
  const { data: conflictCount } = useQuery(
    ['conflict-count', user?.currentOrganizationId],
    conflictService.getConflictCount,
    {
      enabled: !!user?.currentOrganizationId,
      keepPreviousData: true,
      staleTime: 10 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchInterval: (data) => data === null ? 3000 : false, // Poll if cache wasn't ready
    }
  );

  // Fetch standings for all league assignments in a single batch request
  const allAssignmentsList = players.flatMap(p =>
    p.leagueAssignments.map(a => ({ assignmentId: a.id, leagueId: a.league.id, teamName: a.teamName }))
  );
  const totalAssignments = allAssignmentsList.length;
  const playerIds = players.map(p => p.id).join(',');
  const { data: standingsData = {}, isLoading: standingsLoading } = useQuery(
    ['standings-dashboard', user?.currentOrganizationId, playerIds, totalAssignments],
    async () => {
      const batchRequests = allAssignmentsList.map(a => ({
        leagueId: a.leagueId,
        teamNames: [a.teamName],
        limit: totalAssignments === 1 ? 6 : 5,
      }));

      const { results } = await standingsService.getBatchContextualStandings(batchRequests);

      return allAssignmentsList.reduce((acc, assignment, index) => {
        const result = results[index];
        acc[assignment.assignmentId] = {
          assignmentId: assignment.assignmentId,
          leagueId: assignment.leagueId,
          contextualStandings: result?.contextualStandings ?? [],
          teamData: result?.contextualStandings?.find((team: any) => team.isUserTeam === true) || null,
        };
        return acc;
      }, {} as Record<string, any>);
    },
    {
      enabled: players.length > 0,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );

  // Show skeleton layout while players load — avoids white flash on cold start
  if (playersLoading) {
    return (
      <Container sx={{ py: 2, px: { xs: 1.5, sm: 3 }, maxWidth: 'none !important' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', minWidth: 0 }}>
          <Box sx={{ mb: 1 }}>
            <Typography variant="h4" component="h1">Dashboard</Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Overview of your players, schedules, and team performance
          </Typography>
          {/* Main layout skeleton — matches real two-column layout */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, sm: 3 },
            width: '100%',
            maxWidth: '100%',
            alignItems: { xs: 'stretch', md: 'stretch' },
          }}>
            {/* Left column skeleton */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 }, flex: 1, minWidth: 0 }}>
              {/* Scoreboard skeleton */}
              <ScoreboardStrip players={undefined} leagues={undefined} matches={undefined} conflicts={undefined} />
              {/* Action cards skeleton */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 3 } }}>
                {[0, 1].map((i) => (
                  <Card key={i} sx={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: '#43A047' }} />
                    <CardContent>
                      <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
                      <Skeleton variant="text" width="80%" />
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="50%" />
                    </CardContent>
                  </Card>
                ))}
              </Box>
              {/* League Performance skeleton — mobile/tablet (below xl) */}
              <Card sx={{ display: { xs: 'block', xl: 'none' }, width: '100%', position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: '#43A047' }} />
                <CardContent>
                  <Skeleton variant="text" width="50%" height={32} sx={{ mb: 2 }} />
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Skeleton variant="circular" width={50} height={50} />
                    <Box flex={1}>
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="40%" />
                    </Box>
                  </Box>
                  <Skeleton variant="rectangular" height={80} />
                </CardContent>
              </Card>
            </Box>
            {/* Right column skeleton — League Performance on xl+ */}
            <Card sx={{
              display: { xs: 'none', xl: 'flex' },
              flexDirection: 'column',
              minWidth: 0,
              width: { xs: '100%', md: '40%' },
              flex: { xs: 'none', md: '0 0 40%' },
              alignSelf: 'stretch',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: '#43A047' }} />
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', pb: 2 }}>
                <Skeleton variant="text" width="50%" height={32} sx={{ mb: 2 }} />
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Skeleton variant="circular" width={50} height={50} />
                  <Box flex={1}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" />
                  </Box>
                </Box>
                <Skeleton variant="rectangular" sx={{ flex: 1, minHeight: 80 }} />
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Container>
    );
  }

  // Count visible conflicts from fast cache endpoint
  // undefined = still loading (shows skeleton), number = loaded
  // null from API means cache not ready yet — treat as still loading
  const visibleConflicts = players.length === 0
    ? 0
    : conflictCount !== undefined && conflictCount !== null
      ? conflictCount
      : undefined;

  const totalPlayers = players.length;
  const totalLeagues = players.reduce((acc, player) => {
    const leagueIds = player.leagueAssignments.map(a => a.league.id);
    return acc + leagueIds.filter((id, index) => leagueIds.indexOf(id) === index).length;
  }, 0);

  const nextMatch = matchSummary?.nextMatch ?? null;
  // On mobile, compress quick actions into a single row when user has multiple players or leagues
  const compactActions = isMobile && (totalPlayers > 1 || totalLeagues > 1);

  // Use the combined summary endpoint for the scoreboard
  const totalRemainingMatches = matchSummary?.count;

  const formatMatchTime = (dateTime: string | null) => {
    if (!dateTime) return 'Time TBD';
    return dayjs(dateTime).format('dddd, MMMM D, YYYY [at] h:mm A');
  };

  return (
    <Container sx={{ py: 2, px: { xs: 1.5, sm: 3 }, maxWidth: 'none !important' }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'flex-start',
        width: '100%',
        minWidth: 0
      }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="h4" component="h1">
            Dashboard
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Overview of your players, schedules, and team performance
        </Typography>

        {/* Main Layout - Left column for cards, Right column for League Performance */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, sm: 3 }, 
          width: '100%',
          maxWidth: '100%',
          alignItems: { xs: 'stretch', md: 'stretch' }
        }}>
          {/* Left Column - Stats and Actions */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: { xs: 2, sm: 3 }, 
            flex: 1,
            minWidth: 0
          }}>
            {/* Scoreboard Strip */}
            <ScoreboardStrip
              players={totalPlayers}
              leagues={totalLeagues}
              matches={matchSummaryLoading ? undefined : totalRemainingMatches}
              conflicts={visibleConflicts}
            />

            {/* Bottom Row - Actions */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 3 }, alignItems: 'stretch' }}>
              <Card sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: { xs: '100%', sm: 'none' },
                position: 'relative',
                overflow: 'hidden',
              }}>
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: '#43A047' }} />
                <CardContent>
                  {totalPlayers === 0 ? (
                    // Welcome message for new accounts
                    <Box>
                      <Typography variant="h6" component="div" display="flex" alignItems="center" gap={1} gutterBottom>
                        <FlashOn />
                        Welcome to Player Central!
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Get started by adding your first player and assigning them to a league.
                      </Typography>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<People />}
                        onClick={() => navigate('/players')}
                        sx={{ mb: 1 }}
                      >
                        Add Your First Player
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<SportsSoccer />}
                        onClick={() => navigate('/leagues')}
                      >
                        Set Up Leagues
                      </Button>
                    </Box>
                  ) : (
                    // Quick actions for existing users
                    <Box>
                      <Typography variant="h6" component="div" display="flex" alignItems="center" gap={1} gutterBottom>
                        <FlashOn />
                        Quick Actions
                      </Typography>
                      {compactActions ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="outlined"
                              sx={{ flex: 1, minWidth: 0, color: 'rgba(0,0,0,0.87)', borderColor: 'rgba(0,0,0,0.2)', '&:hover': { borderColor: 'rgba(0,0,0,0.3)', bgcolor: 'rgba(0,0,0,0.04)' } }}
                              startIcon={<PersonAdd sx={{ color: 'rgba(0,0,0,0.87)' }} />}
                              onClick={() => navigate('/players/new')}
                            >
                              Player
                            </Button>
                            <Button
                              variant="outlined"
                              sx={{ flex: 1, minWidth: 0, color: 'rgba(0,0,0,0.87)', borderColor: 'rgba(0,0,0,0.2)', '&:hover': { borderColor: 'rgba(0,0,0,0.3)', bgcolor: 'rgba(0,0,0,0.04)' } }}
                              startIcon={<LibraryAdd sx={{ color: 'rgba(0,0,0,0.87)' }} />}
                              onClick={() => navigate('/leagues?action=add')}
                            >
                              League
                            </Button>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="outlined"
                              sx={{ flex: 1, minWidth: 0, color: 'rgba(0,0,0,0.87)', borderColor: 'rgba(0,0,0,0.2)', '&:hover': { borderColor: 'rgba(0,0,0,0.3)', bgcolor: 'rgba(0,0,0,0.04)' } }}
                              startIcon={<CalendarToday sx={{ color: 'rgba(0,0,0,0.87)' }} />}
                              onClick={() => navigate('/schedule?view=calendar')}
                            >
                              Events
                            </Button>
                            <Button
                              variant="outlined"
                              sx={{ flex: 1, minWidth: 0, color: 'rgba(0,0,0,0.87)', borderColor: 'rgba(0,0,0,0.2)', '&:hover': { borderColor: 'rgba(0,0,0,0.3)', bgcolor: 'rgba(0,0,0,0.04)' } }}
                              startIcon={<PostAdd sx={{ color: 'rgba(0,0,0,0.87)' }} />}
                              onClick={() => navigate('/schedule?action=add')}
                            >
                              Match
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Button
                              fullWidth
                              variant="outlined"
                              sx={{ color: 'rgba(0,0,0,0.87)', borderColor: 'rgba(0,0,0,0.2)', '&:hover': { borderColor: 'rgba(0,0,0,0.3)', bgcolor: 'rgba(0,0,0,0.04)' } }}
                              startIcon={<PersonAdd sx={{ color: 'rgba(0,0,0,0.87)' }} />}
                              onClick={() => navigate('/players/new')}
                            >
                              Add Player
                            </Button>
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              fullWidth
                              variant="outlined"
                              sx={{ color: 'rgba(0,0,0,0.87)', borderColor: 'rgba(0,0,0,0.2)', '&:hover': { borderColor: 'rgba(0,0,0,0.3)', bgcolor: 'rgba(0,0,0,0.04)' } }}
                              startIcon={<EmojiEvents sx={{ color: 'rgba(0,0,0,0.87)' }} />}
                              onClick={() => navigate('/leagues?action=add')}
                            >
                              Add League
                            </Button>
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              fullWidth
                              variant="outlined"
                              sx={{ color: 'rgba(0,0,0,0.87)', borderColor: 'rgba(0,0,0,0.2)', '&:hover': { borderColor: 'rgba(0,0,0,0.3)', bgcolor: 'rgba(0,0,0,0.04)' } }}
                              startIcon={<CalendarToday sx={{ color: 'rgba(0,0,0,0.87)' }} />}
                              onClick={() => navigate('/schedule?view=calendar')}
                            >
                              View Calendar
                            </Button>
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              fullWidth
                              variant="outlined"
                              sx={{ color: 'rgba(0,0,0,0.87)', borderColor: 'rgba(0,0,0,0.2)', '&:hover': { borderColor: 'rgba(0,0,0,0.3)', bgcolor: 'rgba(0,0,0,0.04)' } }}
                              startIcon={<PostAdd sx={{ color: 'rgba(0,0,0,0.87)' }} />}
                              onClick={() => navigate('/schedule?action=add')}
                            >
                              Add Match
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>

              <Card sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: { xs: '100%', sm: 'none' },
                position: 'relative',
                overflow: 'hidden',
              }}>
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: '#43A047' }} />
                <CardContent>
                  <Typography variant="h6" component="div" display="flex" alignItems="center" gap={1} gutterBottom>
                    <SportsSoccer />
                    Next Match
                  </Typography>
                  {matchSummaryLoading ? (
                    <Box>
                      <Skeleton variant="text" width="80%" height={32} sx={{ mb: 2 }} />
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="50%" />
                      <Skeleton variant="text" width="40%" />
                    </Box>
                  ) : (
                    <Fade in>
                      {nextMatch ? (
                        <Box>
                          <Typography variant="h5" mb={2}>
                            {nextMatch.homeTeam} vs {nextMatch.awayTeam}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {formatMatchTime(nextMatch.dateTime)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {nextMatch.location || 'TBD'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {nextMatch.league.name}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No upcoming matches
                        </Typography>
                      )}
                    </Fade>
                  )}
                </CardContent>
              </Card>
            </Box>

            {/* Weekly Calendar - Mobile only */}
            <Box sx={{ display: { xs: 'block', sm: 'none' }, width: '100%' }}>
              <WeeklyCalendar />
            </Box>

            {/* League Performance - Below other cards on smaller screens */}
            {totalPlayers > 0 && (
              <Card sx={{
                display: { xs: 'block', xl: 'none' },
                width: '100%',
                position: 'relative',
                overflow: 'hidden',
              }}>
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: '#43A047' }} />
                <CardContent>
                  <Typography variant="h6" component="div" display="flex" alignItems="center" gap={1} gutterBottom>
                    <SportsSoccer />
                    League Performance
                  </Typography>
                  {standingsLoading ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 2, sm: 3 } }}>
                      {players.flatMap(p => p.leagueAssignments).map((_, i) => (
                        <Box key={i}>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Skeleton variant="circular" width={50} height={50} />
                            <Box flex={1}>
                              <Skeleton variant="text" width="60%" />
                              <Skeleton variant="text" width="40%" />
                            </Box>
                          </Box>
                          <Skeleton variant="rectangular" height={80} />
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Fade in>
                    <Box>
                    {players.length > 0 ? (
                    (() => {
                      const allAssignments = players.flatMap(player =>
                        player.leagueAssignments.map(assignment => ({ player, assignment }))
                      );
                      const isSingleLeague = allAssignments.length === 1;

                      if (isSingleLeague) {
                        // Single league: show full contextual standings bar list
                        const { player, assignment } = allAssignments[0];
                        const data = standingsData[assignment.id];
                        const contextual = data?.contextualStandings;

                        return (
                          <Box>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              {(assignment.league as any).logoPath || assignment.league.logoUrl ? (
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                  <LeagueLogo
                                    key={`${assignment.league.id}-${assignment.league.logoPath || assignment.league.logoUrl}`}
                                    leagueName={assignment.league.name}
                                    logoPath={assignment.league.logoPath}
                                    logoUrl={assignment.league.logoUrl}
                                    size={50}
                                  />
                                  <Box sx={{ position: 'absolute', bottom: 5, right: -5 }}>
                                    <PlayerAvatar
                                      playerId={player.id}
                                      player={player}
                                      size="small"
                                      sx={{ width: 25, height: 25, border: '2px solid white', borderRadius: '50%' }}
                                    />
                                  </Box>
                                </Box>
                              ) : (
                                <PlayerAvatar
                                  playerId={player.id}
                                  player={player}
                                  size="small"
                                  sx={{ width: 50, height: 50 }}
                                />
                              )}
                              <Box flex={1} minWidth={0}>
                                <Typography variant="body1" sx={{ fontWeight: 'medium', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {player.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {assignment.league.name}
                                </Typography>
                              </Box>
                            </Box>
                            {contextual && contextual.length > 0 ? (
                              <StandingsBarList teams={contextual} standingsSortBy={assignment.league.standingsSortBy} />
                            ) : data?.teamData ? (
                              <StandingsBarList
                                teams={[{
                                  position: data.teamData.position || assignment.position || 'N/A',
                                  teamName: data.teamData.teamName || assignment.teamName,
                                  won: data.teamData.won || 0,
                                  drawn: data.teamData.drawn || 0,
                                  lost: data.teamData.lost || 0,
                                  points: data.teamData.points || 0,
                                  played: data.teamData.played || 0,
                                  isUserTeam: true,
                                }]}
                                standingsSortBy={assignment.league.standingsSortBy}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">No standings data available</Typography>
                            )}
                          </Box>
                        );
                      }

                      // Multiple leagues: each shows header + only user's team bar row
                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          {allAssignments.map(({ player, assignment }, index) => {
                            const data = standingsData[assignment.id];
                            const contextual = data?.contextualStandings;
                            const userTeam = contextual?.find((t: any) => t.isUserTeam) || data?.teamData;

                            return (
                              <React.Fragment key={`${player.id}-${assignment.id}`}>
                                {index > 0 && <Divider sx={{ my: 1.5 }} />}
                                <Box>
                                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                  {(assignment.league as any).logoPath || assignment.league.logoUrl ? (
                                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                      <LeagueLogo
                                        key={`${assignment.league.id}-${assignment.league.logoPath || assignment.league.logoUrl}`}
                                        leagueName={assignment.league.name}
                                        logoPath={assignment.league.logoPath}
                                        logoUrl={assignment.league.logoUrl}
                                        size={40}
                                      />
                                      <Box sx={{ position: 'absolute', bottom: 3, right: -5 }}>
                                        <PlayerAvatar
                                          playerId={player.id}
                                          player={player}
                                          size="small"
                                          sx={{ width: 20, height: 20, border: '2px solid white', borderRadius: '50%' }}
                                        />
                                      </Box>
                                    </Box>
                                  ) : (
                                    <PlayerAvatar
                                      playerId={player.id}
                                      player={player}
                                      size="small"
                                      sx={{ width: 40, height: 40 }}
                                    />
                                  )}
                                  <Box flex={1} minWidth={0}>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {assignment.league.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                      {player.name}{assignment.league.division ? ` - ${assignment.league.division}` : ''}
                                    </Typography>
                                  </Box>
                                </Box>
                                {userTeam ? (
                                  <StandingsBarList
                                    teams={[{
                                      position: userTeam.position || assignment.position || 'N/A',
                                      teamName: userTeam.teamName || assignment.teamName,
                                      won: userTeam.won || 0,
                                      drawn: userTeam.drawn || 0,
                                      lost: userTeam.lost || 0,
                                      points: userTeam.points || 0,
                                      played: userTeam.played || 0,
                                    }]}
                                    standingsSortBy={assignment.league.standingsSortBy}
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>No standings data</Typography>
                                )}
                                </Box>
                              </React.Fragment>
                            );
                          })}
                        </Box>
                      );
                    })()
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No league assignments found
                    </Typography>
                  )}
                    </Box>
                    </Fade>
                  )}
                </CardContent>
              </Card>
            )}

          </Box>

          {/* Right Column - League Performance (1280px+ only) */}
          <Card sx={{
            display: { xs: 'none', xl: 'flex' },
            flexDirection: 'column',
            minWidth: 0,
            width: { xs: '100%', md: '40%' },
            flex: { xs: 'none', md: '0 0 40%' },
            alignSelf: 'stretch',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: '#43A047' }} />
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', pb: 2, minWidth: 0 }}>
              <Typography variant="h6" component="div" display="flex" alignItems="center" gap={1} mb={2}>
                <SportsSoccer />
                League Performance
              </Typography>
              {standingsLoading ? (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  {players.flatMap(p => p.leagueAssignments).slice(0, 2).map((_, i) => (
                    <Box key={i} sx={{ flex: 1, display: 'flex', flexDirection: 'column', mb: i === 0 ? 2 : 0 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Skeleton variant="circular" width={50} height={50} />
                        <Box flex={1}>
                          <Skeleton variant="text" width="60%" />
                          <Skeleton variant="text" width="40%" />
                        </Box>
                      </Box>
                      <Skeleton variant="rectangular" sx={{ flex: 1, minHeight: 80 }} />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Fade in>
                {players.length > 0 ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <LeaguePerformanceCarousel
                      assignments={players.flatMap(player =>
                        player.leagueAssignments.map(assignment => ({ player, assignment }))
                      )}
                      standingsData={standingsData}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No league assignments found
                  </Typography>
                )}
                </Fade>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Weekly Calendar - positioned at bottom, spanning full width (hidden on mobile where it appears above League Performance) */}
        <Box sx={{ mt: 3, width: '100%', display: { xs: 'none', sm: 'block' } }}>
          <WeeklyCalendar />
        </Box>
      </Box>

      {/* Welcome Dialog */}
      <Dialog 
        open={welcomeDialogOpen} 
        onClose={() => setWelcomeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'success.main' }}>
              <CheckCircle />
            </Avatar>
            <Typography variant="h5">
              Welcome to Player Central!
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Congratulations! Your organization has been created successfully with a free tier subscription. 
            You're now ready to start managing your soccer schedules.
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Get started with these next steps:
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon>
                <PersonAdd color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Add Players"
                secondary="Create player profiles for your family members or team"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <EmojiEvents color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Connect Leagues"
                secondary="Link your leagues from GotSport, SincSports, or add them manually"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Settings color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Customize Settings"
                secondary="Set up your organization profile and invite other family members"
              />
            </ListItem>
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Free Tier Benefits:</strong> Track up to 10 players across 10 leagues with basic scheduling features.
              Upgrade anytime to unlock travel-aware conflicts, weather, departure times, and analytics.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setWelcomeDialogOpen(false)} startIcon={<CloseIcon />}>
            I'll explore on my own
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setWelcomeDialogOpen(false);
              navigate('/players');
            }}
          >
            Add My First Player
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DashboardPage;
